import { Sort, SortDirection } from '../../core/models/dynacrud';

/**
 * THE ONE DERIVATION OF "WHICH WAY IS THIS COLUMN SORTED".
 *
 * Nine components answered this question for themselves, inside their own `sortArrow()`, and each
 * one was a second copy of the same condition. That was survivable while the answer only chose a
 * glyph. It is not survivable now that `aria-sort` is derived from it too: two independent
 * derivations of one fact are how a sighted user and a screen-reader user come to be told
 * different things about the same column. So each `sortArrow()` delegates here, and the component
 * reads `aria-sort` from the same call.
 *
 * Three shapes, because the tree holds three and no more: a paged `"field,dir"` string, an
 * `{active, direction}` object whose `'none'` means unsorted, and the two core tables' multi-sort
 * entries (`{direction, position}`, one per sorted column).
 */
export type SortArrowDirection = 'asc' | 'desc' | null;

/**
 * A paged state's direction for one field. `'asc'` is read exactly; anything else present is
 * `'desc'`, which is what every call site already did — `curDir === 'asc' ? '▲' : '▼'` renders the
 * down arrow for a malformed or absent direction rather than nothing, and this keeps that.
 *
 * An EMPTY state matches no field, including the empty field name: `''.split(',')` answers `['']`,
 * so a nameless column would otherwise read as sorted.
 */
export function directionFromPagedSort(
  sort: string | null | undefined,
  field: string,
): SortArrowDirection {
  if (!sort || !field) {
    return null;
  }
  const [activeField, direction] = sort.split(',');
  if (activeField !== field) {
    return null;
  }
  return direction === 'asc' ? 'asc' : 'desc';
}

/** An `{active, direction}` state's direction for one column. `'none'` is not sorted. */
export function directionFromActiveSort(
  active: string | null | undefined,
  direction: string | null | undefined,
  column: string,
): SortArrowDirection {
  if (!active || active !== column || !direction || direction === 'none') {
    return null;
  }
  return direction === 'asc' ? 'asc' : 'desc';
}

/** One column of a MULTI-sort: its direction and its priority, where `position` 0 is the primary sort. */
export interface PrioritisedSort {
  direction: SortDirection;
  position: number;
}

/**
 * A multi-sort entry's direction AS `aria-sort` REPORTS IT: the PRIMARY sort only.
 *
 * ARIA allows `aria-sort` on one header at a time, and a screen reader meeting three "ascending"
 * headers has no way to tell which one orders the rows. So only `position` 0 answers a direction;
 * a secondary sort is spoken by the header's own text instead (`secondarySortRank`), and its arrow
 * and badge — which read the entry directly — are untouched.
 */
export function directionFromPrioritisedSort(
  item: PrioritisedSort | null | undefined,
): SortArrowDirection {
  if (!item || item.position !== 0) {
    return null;
  }
  return item.direction === SortDirection.ASC ? 'asc' : 'desc';
}

/** A secondary sort's 1-based rank (2, 3, ...), or `null` for the primary sort and an unsorted column. */
export function secondarySortRank(item: PrioritisedSort | null | undefined): number | null {
  return item && item.position > 0 ? item.position + 1 : null;
}

/**
 * A secondary sort's direction, read beside `secondarySortRank`: `null` for the primary sort and an
 * unsorted column. It was re-derived inline at every multi-sort header, each a second copy of the
 * rule `directionFromPrioritisedSort` states for the primary.
 */
export function secondarySortDirection(item: PrioritisedSort | null | undefined): SortArrowDirection {
  if (!item || item.position <= 0) {
    return null;
  }
  return item.direction === SortDirection.ASC ? 'asc' : 'desc';
}

/**
 * A multi-sort rebuilt from the sort the SERVER ECHOED, with its positions COMPACTED.
 *
 * The echo can name a field this table renders no column for (a declared non-column default, a
 * persisted slot from an older column set), and that entry is skipped. Both core tables used to
 * take the echo's own index as the position, so a skipped entry left a GAP: the user's only sorted
 * column came back at position 1, `directionFromPrioritisedSort` answered null, every header said
 * `aria-sort="none"`, and the hidden text announced "secondary sort 2" with no first. `setSort`
 * already renumbers after a press; this is the same rule on the read side.
 *
 * A column that cannot be sorted counts as no column at all, and that is the CALLER's filter: the
 * core table passes its sortable fields only, so an echoed non-sortable column (a default sort on a
 * column the list displays but does not let the user sort) cannot take position 0 and push the
 * user's own sort down to "secondary 2". `table-edit-rows` decides sortability per TABLE, not per
 * field, so it passes every field and this function does not read `sortable` itself.
 */
export function prioritisedSortFromEcho<F extends { id: string; sortExpression: string }>(
  sort: Sort[] | null | undefined,
  fields: F[] | null | undefined,
): { [id: string]: PrioritisedSort & { field: F } } {
  const out: { [id: string]: PrioritisedSort & { field: F } } = {};
  let position = 0;
  for (const s of sort || []) {
    const field = fields?.find((f) => f.sortExpression === s.field);
    if (field && !out[field.id]) {
      out[field.id] = {
        field,
        direction: SortDirection[String(s.direction).toUpperCase() as SortDirection],
        position: position++,
      };
    }
  }
  return out;
}

/** The arrow the tree already renders. Kept here so the refactor moves no rendered character. */
export function sortGlyph(direction: SortArrowDirection): string {
  if (direction === 'asc') {
    return '▲';
  }
  return direction === 'desc' ? '▼' : '';
}
